import { getActiveSession } from '~/domain/session/activeSession';
import type { DestinationRoi } from './applyComputedGeometry';

/**
 * Record a geometry write as a tracked contour edit on the destination ROI.
 *
 * `dicomUid` must identify the CURRENTLY active structure set — the one the
 * destination was just created in — because a roiNumber is unique only within a
 * structure set. Resolving it against a different (e.g. previously selected) set
 * would mark an unrelated, real ROI as edited.
 *
 * Best effort: with no active session or structure set the geometry write stays
 * applied but unrecorded (no dirty/persist + audit flag) rather than failing the
 * operation. The skip is deliberate and surfaced, never silent.
 */
export function recordContourEdit(
    dicomUid: string | undefined,
    dest: DestinationRoi
): void {
    const session = getActiveSession();
    if (!session || !dicomUid) {
        // Deliberate, safe no-op: the write already landed; only the
        // audit/persist bookkeeping is skipped. No PHI in the message.
        console.warn(
            '[agent] contour edit not recorded: no active session or ' +
                'structure set'
        );
        return;
    }
    // commitContourEdit re-resolves the ROI by domain id across all structure
    // sets, so it can throw StaleReferenceError on a concurrent delete/race. The
    // geometry write has already landed and is visible; recording is best effort
    // and must never turn a successful write into a reported failure.
    try {
        const structureSet =
            session.patient.getStructureSetWithSopInstanceUid(dicomUid);
        const roi = structureSet?.rois.find(
            (r) => r.roiNumber === dest.roiNumber
        );
        if (roi) {
            session.contourService.commitContourEdit({ id: roi.id }, []);
        }
    } catch (e) {
        // Redact: an error message could carry a ROI id or structure name.
        console.warn(
            '[agent] failed to record contour edit:',
            e instanceof Error ? e.name : 'unknown'
        );
    }
}
